"use client"

import { ApertureStyles, T, FM, MAXW, SectionEyebrow, Check, Dash, Half } from "./_shared"

export default function ApertureCompare() {
  const cols = ["DataBridge", "Credit validators", "Spreadsheets"]
  const rows: { l: string; v: (boolean | "partial" | string)[] }[] = [
    { l: "Bulk CSV cleaning",             v: [true, true, "manual"] },
    { l: "Live MX + format checks",       v: [true, true, false] },
    { l: "Duplicate / role / disposable", v: [true, "partial", "manual"] },
    { l: "Likely typo suggestions",       v: [true, "partial", false] },
    { l: "Per-row reason column",         v: [true, "partial", false] },
    { l: "Export mapper for ESP columns", v: [true, false, "manual"] },
    { l: "Compare two lists",             v: [true, "partial", "manual"] },
    { l: "Simple monthly row allowance",  v: ["1k / 100k / 1M", "credit-based", false] },
  ]

  return (
    <section id="compare" className="relative">
      <ApertureStyles />
      <div className={`${MAXW} mx-auto px-6 lg:px-10 py-12 sm:py-16 md:py-24`}>
        <SectionEyebrow>Why DataBridge</SectionEyebrow>
        <h2
          className="mt-3 leading-[1.0] tracking-[-0.035em] max-w-3xl"
          style={{ fontSize: "clamp(2rem, 4vw, 3.1rem)", fontWeight: 800 }}
        >
          Less credit math.{" "}
          <em style={{ color: T.acc, fontStyle: "italic", fontWeight: 700 }}>More list decisions.</em>
        </h2>
        <p className="mt-5 max-w-[62ch]" style={{ fontSize: 15.5, color: T.ink2, lineHeight: 1.6 }}>
          Most validators stop at a score or a status. DataBridge is built around the work that happens
          after validation: explain the row, fix obvious typos, reshape the export, and compare it with
          another list without opening a spreadsheet.
        </p>

        <div className="mt-12 rounded-2xl overflow-x-auto overflow-y-hidden" style={{
          background: T.surface,
          border: `1px solid ${T.hair}`,
          boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset, 0 24px 60px -32px rgba(10,10,10,0.18)`,
          WebkitOverflowScrolling: "touch",
        }}>
          <div style={{ minWidth: 680 }}>
            <div
              className="grid"
              style={{
                gridTemplateColumns: "minmax(230px,1.5fr) repeat(3, minmax(130px,1fr))",
                borderBottom: `1px solid ${T.hair}`,
              }}
            >
              <div className="px-5 py-4">
                <span style={{ fontFamily: FM, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: T.ink3 }}>
                  Workflow
                </span>
              </div>
              {cols.map((c, i) => (
                <div
                  key={c}
                  className="px-5 py-4"
                  style={{ borderLeft: `1px solid ${T.hair}`, background: i === 0 ? T.accSoft : undefined }}
                >
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: i === 0 ? T.acc : T.ink, letterSpacing: "-0.01em" }}>
                    {c}
                    {i === 0 && (
                      <span
                        className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded"
                        style={{
                          fontFamily: FM, fontSize: 9.5, background: T.accChip, color: T.acc,
                          letterSpacing: "0.1em", textTransform: "uppercase",
                        }}
                      >
                        ours
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>

            {rows.map((r, ri) => (
              <div
                key={r.l}
                className="grid"
                style={{
                  gridTemplateColumns: "minmax(230px,1.5fr) repeat(3, minmax(130px,1fr))",
                  borderBottom: ri < rows.length - 1 ? `1px solid ${T.hair}` : undefined,
                }}
              >
                <div className="px-5 py-4" style={{ fontSize: 13.5, color: T.ink2 }}>{r.l}</div>
                {r.v.map((cell, ci) => (
                  <div
                    key={ci}
                    className="px-5 py-4 flex items-center"
                    style={{ borderLeft: `1px solid ${T.hair}`, background: ci === 0 ? T.accSoft : undefined }}
                  >
                    <Cell value={cell} highlight={ci === 0} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <p className="mt-3 sm:hidden" style={{ fontSize: 11, color: T.ink3, fontFamily: FM, letterSpacing: "0.06em" }}>
          ← Scroll to compare workflows →
        </p>
        <p className="mt-5 max-w-[70ch]" style={{ fontSize: 12, color: T.ink3, lineHeight: 1.55 }}>
          This table compares workflows, not every vendor SKU. If you already have a validator you like,
          DataBridge still helps when you need an audited CSV, ESP-ready columns, or a quick list compare.
        </p>
      </div>
    </section>
  )
}

function Cell({ value, highlight }: { value: boolean | "partial" | string; highlight: boolean }) {
  if (value === true) {
    return (
      <span style={{ color: highlight ? T.acc : T.ink, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: highlight ? 600 : 500 }}>
        <Check />
        Yes
      </span>
    )
  }
  if (value === false) {
    return <span style={{ color: T.ink3, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5 }}><Dash /> No</span>
  }
  if (value === "partial") {
    return <span style={{ color: T.warn, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5 }}><Half /> Sometimes</span>
  }
  return (
    <span
      className="tabular-nums"
      style={{ fontFamily: FM, fontSize: 12.5, color: highlight ? T.acc : T.ink2, fontWeight: highlight ? 700 : 500 }}
    >
      {value}
    </span>
  )
}
